import { useEffect, useMemo, useState } from "react";
import {
  DMAutomation,
  DMAutomationContent,
  DMAutomationTrigger,
} from "@markit/common.types";
import { getDoc, getDocs, onSnapshot, orderBy, query } from "../../firebase";
import {
  getUserDmAutomationContentsRef,
  getUserDmAutomationRef,
  getUserDmAutomationTriggersRef,
} from "../../utils/FirebaseUtils";

type useLiveUpdatingDMAutomationProps = {
  initialDMAutomationId: string;
  userId: string;
};

export const useLiveUpdatingDMAutomation = (
  props: useLiveUpdatingDMAutomationProps
) => {
  const { initialDMAutomationId, userId } = props;
  const [fetchedDMAutomation, setFetchedDMAutomation] =
    useState<DMAutomation>();
  const [fetchedDMAutomationTriggers, setFetchedDMAutomationTriggers] =
    useState<DMAutomationTrigger[]>();
  const [fetchedDMAutomationContents, setFetchedDMAutomationContents] =
    useState<DMAutomationContent[]>();
  const [isError, setIsError] = useState(false);

  const loaded = useMemo(
    () =>
      fetchedDMAutomation !== undefined &&
      fetchedDMAutomationTriggers !== undefined &&
      fetchedDMAutomationContents !== undefined,
    [
      fetchedDMAutomation,
      fetchedDMAutomationTriggers,
      fetchedDMAutomationContents,
    ]
  );

  useEffect(() => {
    // Reset so `loaded`/the displayed data reflect the new id's load status,
    // rather than showing the previous automation's data until it resolves.
    setFetchedDMAutomation(undefined);
    setFetchedDMAutomationTriggers(undefined);
    setFetchedDMAutomationContents(undefined);
    setIsError(false);

    if (!initialDMAutomationId) {
      return;
    }
    const dmAutomationId = initialDMAutomationId;

    let isMounted = true;
    const unsubscribes: (() => void)[] = [];

    (async () => {
      let dmAutomationSnap;
      try {
        dmAutomationSnap = await getDoc(
          getUserDmAutomationRef(userId, dmAutomationId)
        );
      } catch (error) {
        console.error("Error loading user dmAutomation", error);
        return;
      }
      if (!isMounted) return;
      if (!dmAutomationSnap.exists()) {
        setIsError(true);
        return;
      }
      setIsError(false);

      // Fetch DMAutomation and listen for changes
      const unsubscribeDmAutomation = onSnapshot(
        getUserDmAutomationRef(userId, dmAutomationId),
        (doc) => {
          if (!isMounted) return;
          const dmAutomationData = doc.data();
          if (dmAutomationData) {
            setFetchedDMAutomation(dmAutomationData);
          }
        }
      );
      unsubscribes.push(unsubscribeDmAutomation);

      // Fetch DMAutomationTriggers (newest first) and listen for changes
      const dmAutomationTriggersQuery = query(
        getUserDmAutomationTriggersRef(userId, dmAutomationId),
        orderBy("createdAt", "desc")
      );
      const dmAutomationTriggersSnap = await getDocs(dmAutomationTriggersQuery);
      if (!isMounted) return;
      setFetchedDMAutomationTriggers(
        dmAutomationTriggersSnap.docs.map((doc) => doc.data())
      );

      const unsubscribeTriggers = onSnapshot(
        dmAutomationTriggersQuery,
        (snapshot) => {
          if (!isMounted) return;
          setFetchedDMAutomationTriggers(
            snapshot.docs.map((doc) => doc.data())
          );
        }
      );
      unsubscribes.push(unsubscribeTriggers);

      // Fetch DMAutomationContents and listen for changes
      const dmAutomationContentsQuery = query(
        getUserDmAutomationContentsRef(userId, dmAutomationId),
        orderBy("createdAt", "asc")
      );
      const dmAutomationContentsSnap = await getDocs(dmAutomationContentsQuery);
      if (!isMounted) return;
      setFetchedDMAutomationContents(
        dmAutomationContentsSnap.docs.map((doc) => doc.data())
      );

      const unsubscribeContents = onSnapshot(
        dmAutomationContentsSnap.query,
        (snapshot) => {
          if (!isMounted) return;
          setFetchedDMAutomationContents(
            snapshot.docs.map((doc) => doc.data())
          );
        }
      );
      unsubscribes.push(unsubscribeContents);
    })();

    return () => {
      isMounted = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [initialDMAutomationId, userId]);

  return {
    liveDMAutomation: fetchedDMAutomation,
    liveDMAutomationTriggers: fetchedDMAutomationTriggers,
    liveDMAutomationContents: fetchedDMAutomationContents,
    isLiveDMAutomationError: isError,
    loaded: loaded,
  };
};
